import { Alert, Box, CircularProgress, Dialog, Stack, Tab, Tabs, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { colors, CommonModal, OtpInput, PasswordSetup, TextFieldComponent, theme } from '../../components';
import CustomButton from '../../components/button/ButtonComponent';
import { ContactSupportModalContent } from '../../components/contact-support/ContactSupportModalContent';
import { CommonLoginWrapper, LinkTextWrapper, LoginTabsWrapper } from '../../components/global-styles/GlobalStyles';
import { PhoneInputComponent } from '../../components/text-field/PhoneInputComponent';
import { BrandLogo } from '../../components/ui-components/BrandLogo';
import LoginPasswordStrengthField from '../../components/ui-components/LoginPasswordStrengthField';
import { useContracts } from '../../hooks/useContracts';
import { AUTOPAY_CONTRACTID_STORAGE_KEY } from '../../pages/contracts/AutoPayLink';
import { chatbotService } from '../../services/chatbot-service';
import { ContractApiResponse } from '../../services/contract-services';
import {
    forgotLoginPassword,
    LoginRequest,
    LoginResponse,
    confirmSelfVerifyPhoneOtp,
    resendLoginOtp,
    resetForgotPassword,
    sendLoginOtp,
    sendSelfVerifyPhoneOtp,
    userLogin,
    verifyForgotPasswordOtp,
    verifyLoginOtp,
} from '../../services/login-service';
import { RootState } from '../../store';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { initializeSession, resetChatbot } from '../../store/reducers/chatbotSlice';
import { clearImpersonation } from '../../store/reducers/impersonationSlice';
import { openToast, resetToast } from '../../store/reducers/toastSlice';
import { setLoginUserProfileImageUrl, updateLoginStatus, updateLoginUserDetails } from '../../store/reducers/userSlice';
import { markJustLoggedIn } from '../../utils/auth-session-cleanup';
import { setUserId, trackEvent, trackPasswordChange } from '../../utils/google-analytics';
import { defaultTimerCount, getMaskedEmailForProp, getMaskedPhoneNumberForProp, regex } from '../../utils/helper';
import { clearMigrationCookiesForCurrentHost, setCurrentDomainMigrationCookie } from '../../utils/migration-cookies';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';
import {
    FooterLinksContainer,
    FooterLinkText,
    FooterTypography,
    LoginLinkText,
    SeparatorText,
    SupportLinksStack,
    SupportText,
} from '../registration/registration-styles';
import { ResendOtpRequest } from '../registration/registration-types';

import { ParsedIdentifier } from './auth-flow/identifier';
import { maskEmail, maskPhone } from './auth-flow/masking';
import { isMergedLoginValid, MergedLoginForm } from './auth-flow/MergedLoginForm';
import { SecurityCodeStep } from './auth-flow/SecurityCodeStep';
import { OtpDeliveryMethod, TwoFactorMethodPicker } from './auth-flow/TwoFactorMethodPicker';
import { UpdatePhoneFlow } from './auth-flow/UpdatePhoneFlow';
import { normalizeEmail } from './auth-flow/validators';

type LoginApiResponse = {
    statusCode: number;
    message?: string;
    data?: {
        token?: string;
        refreshToken?: string;
        user?: Record<string, unknown>;
        isMigrated?: boolean;
        id?: string;
        accessToken?: string;
        otpSent?: boolean;
    };
    token?: string;
    refreshToken?: string;
    user?: Record<string, unknown>;
    isMigrated?: boolean;
    id?: string;
};

interface TabPanelProps {
    children: React.ReactNode;
    index: number;
    value: number;
}

const LOGIN_LOCKOUT_STORAGE_KEY = 'loginLockoutEndTime';
const LOGIN_FAILED_ATTEMPTS_KEY = 'loginFailedAttempts';
const LOGIN_LOCKOUT_CREDENTIAL_KEY = 'loginLockoutCredential'; // Track which credential is locked
// Merged email/mobile login + in-card security code. Set REACT_APP_MERGED_LOGIN=false to fall back to the legacy tabs.
const IS_MERGED_LOGIN = process.env.REACT_APP_MERGED_LOGIN !== 'false';
const LOGIN_OTP_EXPIRY_MINUTES = 5;
/** Backend signals for a mobile number that isn't on file / isn't verified (login by phone). */
const PHONE_NOT_VERIFIED_ERROR_CODES = ['PHONE_NOT_VERIFIED', 'PHONE_NOT_REGISTERED'];
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 5 * 60 * 1000; // 5 minutes

const CustomTabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            overflow="auto"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
            sx={{ pt: 3 }}
        >
            {value === index && <Box>{children}</Box>}
        </Box>
    );
};
export const Login: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { fetchContracts } = useContracts();
    const location = useLocation();
    const [forgotPassword, setForgotPassword] = useState(false);
    const [contractId, setContractId] = useState<string>('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [currentStep, setCurrentStep] = useState<string>('login');
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [passwordOtpSuccess, setPasswordOtpSuccess] = useState(false);
    const [value, setValue] = useState(0);
    const [isLoginThroughEmail, setIsLoginThroughEmail] = useState<boolean | null>(null);
    const [isCalledFromLogin, setIsCalledFromLogin] = useState<boolean>(false);
    const [countryCode, setCountryCode] = useState('+1');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [openContactSupportModal, setOpenContactSupportModal] = useState(false);
    const [phoneNotRegistered, setPhoneNotRegistered] = useState(false);
    // Destinations returned after credentials are verified (code not sent until a method is chosen).
    const [mfaOptions, setMfaOptions] = useState<{
        userId: string;
        email: string;
        phoneNumber: string;
        isPhoneVerified: boolean;
    } | null>(null);
    const [mfaMethod, setMfaMethod] = useState<OtpDeliveryMethod>('email');
    const [isSendingCode, setIsSendingCode] = useState(false);
    // userId from the login response; the self-verify endpoints need it to identify the account.
    const [pendingUserId, setPendingUserId] = useState('');
    const [mobileVerify, setMobileVerify] = useState<{
        userId: string;
        phone: string;
        origin: 'two-factor' | 'login';
    } | null>(null);
    const [otpAttemptCount, setOtpAttemptCount] = useState(0);
    const [isOtpDisabled, setIsOtpDisabled] = useState(false);
    const [timerCount, setTimerCount] = useState(defaultTimerCount);
    const { t } = useTranslation();
    const brandDetails = useSelector((state: RootState) => state.brand.brandDetails);
    const isBrandLoading = useSelector((state: RootState) => state.brand.isBrandLoading);
    const resolvedPortalId = (() => {
        const id = Number(brandDetails?.Id);
        return Number.isFinite(id) && id > 0 ? id : 2;
    })();

    // Shared toast dispatcher - every call site below opens the toast with the same
    // show/isAlertOpen shape, only the message and severity vary.
    const showToast = useCallback(
        (message: string | undefined, severity: string) => {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message,
                    severity,
                }),
            );
        },
        [dispatch],
    );

    // Login lockout: 3 failed attempts → disable login for 5 minutes (per session, sessionStorage)
    const [loginLockoutEndTime, setLoginLockoutEndTime] = useState<number | null>(null);
    const [lockoutRemainingSeconds, setLockoutRemainingSeconds] = useState(0);

    const clearLoginLockout = useCallback(() => {
        sessionStorage.removeItem(LOGIN_LOCKOUT_STORAGE_KEY);
        sessionStorage.removeItem(LOGIN_FAILED_ATTEMPTS_KEY);
        sessionStorage.removeItem(LOGIN_LOCKOUT_CREDENTIAL_KEY);
        setLoginLockoutEndTime(null);
        setLockoutRemainingSeconds(0);
    }, []);

    // Hydrate lockout state from sessionStorage on mount (continuous within session)
    useEffect(() => {
        const storedEnd = sessionStorage.getItem(LOGIN_LOCKOUT_STORAGE_KEY);
        const now = Date.now();
        if (storedEnd) {
            const endTime = parseInt(storedEnd, 10);
            if (now < endTime) {
                setLoginLockoutEndTime(endTime);
                setLockoutRemainingSeconds(Math.ceil((endTime - now) / 1000));
            } else {
                clearLoginLockout();
            }
        }

        // Check for logout toast message
        const logoutToastMessage = sessionStorage.getItem('logoutToastMessage');
        if (logoutToastMessage) {
            showToast(logoutToastMessage, 'error');
            // Clear the message from sessionStorage so it doesn't show again
            sessionStorage.removeItem('logoutToastMessage');
        }
    }, [clearLoginLockout, showToast]);

    // Live countdown: update remaining seconds every second; clear lockout when 0
    useEffect(() => {
        if (loginLockoutEndTime == null) return;
        const tick = () => {
            const now = Date.now();
            const remaining = Math.ceil((loginLockoutEndTime - now) / 1000);
            if (remaining <= 0) {
                clearLoginLockout();
                return;
            }
            setLockoutRemainingSeconds(remaining);
        };
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [loginLockoutEndTime, clearLoginLockout]);

    // FIX: Clear toast on component unmount to prevent toasts from persisting on other screens
    useEffect(() => {
        return () => {
            dispatch(resetToast({ show: false, message: null, isAlertOpen: false }));
        };
    }, [dispatch]);

    // Prefill email from migration page when user is already migrated
    useEffect(() => {
        const locationState = location.state as {
            prefillEmail?: string;
            toastMessage?: string;
            toastSeverity?: string;
        } | null;
        const prefillEmail = locationState?.prefillEmail;
        if (prefillEmail && prefillEmail.trim()) {
            setUsername(prefillEmail.trim());
        }
        // Show toast message from migration page
        if (locationState?.toastMessage) {
            showToast(locationState.toastMessage, locationState.toastSeverity || 'info');
        }
    }, [location, showToast]);

    const isLoginLockedOut = loginLockoutEndTime != null && Date.now() < loginLockoutEndTime;

    // Check if the current input (email/phone) is the one that's locked out
    const getCurrentCredential = () => {
        return value === 0 ? username.trim() : phoneNumber.trim();
    };

    const isCurrentCredentialLockedOut = () => {
        if (!isLoginLockedOut) return false;
        const lockedCredential = sessionStorage.getItem(LOGIN_LOCKOUT_CREDENTIAL_KEY);
        const currentCredential = getCurrentCredential();
        return lockedCredential?.toLowerCase() === currentCredential.toLowerCase();
    };
    const formatLockoutRemaining = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const handleAutopayAfterLogin = useCallback(
        async (contractIdFromStorage: string) => {
            try {
                dispatch(showLoader());
                const response = await fetchContracts(false);

                if (response.statusCode === 200 && response.data && response.data.length > 0) {
                    const matchingContract = response.data.find(
                        (contract: ContractApiResponse) => String(contract.Id) === String(contractIdFromStorage),
                    );

                    if (matchingContract) {
                        // Navigate to contract details with state to auto-open autopay modal
                        navigate(NavigationConfig.ContractDetails, {
                            state: { contract: matchingContract, openAutoPayModal: true },
                        });
                    } else {
                        // Contract not found - fall back to the regular contracts list
                        navigate(NavigationConfig.ContractOverview, { replace: true });
                    }
                } else {
                    // Failed to fetch contracts - fall back to the regular contracts list
                    navigate(NavigationConfig.ContractOverview, { replace: true });
                }
            } catch (err) {
                console.error('Error fetching contracts after login for AutoPay:', err);
                // On error, fall back to the regular contracts list instead of showing an error
                navigate(NavigationConfig.ContractOverview, { replace: true });
            } finally {
                dispatch(hideLoader());
            }
        },
        [navigate, dispatch, fetchContracts],
    );

    const handleLogin = () => {
        setErrors({});
        setCurrentStep('login');
        setForgotPassword(false);
    };

    // Shared field-level validators (reused across the different login/forgot-password forms below)
    const validateEmailField = (email: string, error: Record<string, string>) => {
        if (!email.trim()) {
            error.email = t('login.validation.emailRequired');
        }
        if (email && !regex.emailRegExp.test(email)) {
            error.email = t('login.validation.emailInvalid');
        }
    };

    const validatePasswordField = (pwd: string, error: Record<string, string>) => {
        if (!pwd.trim()) {
            error.password = t('login.validation.passwordRequired');
        }
        if (pwd && !regex.passwordRegExp.test(pwd)) {
            error.password = t('login.validation.passwordInvalid');
        }
    };

    const validatePhoneField = (phone: string, error: Record<string, string>) => {
        if (!phone.trim()) {
            error.phoneNumber = t('login.validation.phoneRequired');
        }
        if (phone && !regex.mobileRegExp.test(phone)) {
            error.phoneNumber = t('login.validation.phoneInvalid');
        }
    };

    const isForgotEmailValid = () => {
        const error: Record<string, string> = {};
        validateEmailField(username, error);
        setErrors(error);
        return Object.keys(error).length === 0;
    };
    const sendRestLink = async () => {
        if (isForgotEmailValid()) {
            // api call to send reset link
            try {
                const sendResetLinkPayload = {
                    email: username,
                    portalId: resolvedPortalId,
                };
                const response = (await forgotLoginPassword(sendResetLinkPayload, true)) as unknown as LoginApiResponse;
                if (response?.data?.isMigrated) {
                    setShowOtpModal(false);
                    // Account migration required, navigate to migration page
                    navigate(NavigationConfig.UpgradeAnnouncement, {
                        state: {
                            migrationUser: true,
                            email: username,
                            portalId: resolvedPortalId,
                        },
                    });
                    return;
                } else if (response.statusCode === 200) {
                    setErrors({});
                    setIsCalledFromLogin(false);
                    // Reset attempt count when opening passcode modal
                    setOtpAttemptCount(0);
                    setIsOtpDisabled(false);
                    setShowOtpModal(true);
                } else if (response.statusCode === 400 || response.message === 'User Not Found') {
                    showToast(response.message || 'User Not Found', 'error');
                } else {
                    showToast(response.message, 'error');
                }
            } catch (error) {
                const err = error as { response?: { data?: { message?: string }; status?: number }; message?: string };
                const errData = err?.response?.data;
                const errMessage = errData?.message || err?.message || 'Error Sending Reset Link';
                showToast(errMessage, 'error');
                console.error('Error sending reset link:', error);
            } finally {
                dispatch(hideLoader());
            }
        }
    };

    const isLoginWithEmailFormValid = () => {
        const error: Record<string, string> = {};
        validateEmailField(username, error);
        validatePasswordField(password, error);
        setErrors(error);
        return Object.keys(error).length === 0;
    };

    const isLoginWithPhoneNumberFormValid = () => {
        const error: Record<string, string> = {};
        validatePhoneField(phoneNumber, error);
        validatePasswordField(password, error);
        setErrors(error);
        return Object.keys(error).length === 0;
    };

    // Helper function to check if form is valid (for button disabled state)
    const isFormValid = () => {
        if (IS_MERGED_LOGIN) {
            return isMergedLoginValid(value === 0 ? 'email' : 'phone', username, phoneNumber, password);
        }
        if (value === 0) {
            // Email tab
            return (
                username.trim() !== '' &&
                regex.emailRegExp.test(username) &&
                password.trim() !== '' &&
                regex.passwordRegExp.test(password)
            );
        } else {
            // Phone tab
            return (
                phoneNumber.trim() !== '' &&
                regex.mobileRegExp.test(phoneNumber) &&
                password.trim() !== '' &&
                regex.passwordRegExp.test(password)
            );
        }
    };

    const recordFailedLoginAttempt = (): boolean => {
        const current = parseInt(sessionStorage.getItem(LOGIN_FAILED_ATTEMPTS_KEY) || '0', 10);
        const next = current + 1;
        sessionStorage.setItem(LOGIN_FAILED_ATTEMPTS_KEY, String(next));
        if (next >= MAX_LOGIN_ATTEMPTS) {
            const endTime = Date.now() + LOCKOUT_DURATION_MS;
            sessionStorage.setItem(LOGIN_LOCKOUT_STORAGE_KEY, String(endTime));
            sessionStorage.setItem(LOGIN_LOCKOUT_CREDENTIAL_KEY, getCurrentCredential());
            setLoginLockoutEndTime(endTime);
            setLockoutRemainingSeconds(Math.ceil(LOCKOUT_DURATION_MS / 1000));
            return true;
        }
        return false;
    };

    // Shared submit logic for both the email and phone-number login tabs. `method`
    // only affects analytics payload shape and which "logged in via" flag gets set - the
    // request/response handling itself is identical between the two tabs.
    const submitLoginRequest = async (loginPayload: LoginRequest, method: 'email' | 'phone') => {
        // Track login attempt - method only, no PII (email/phone) is sent to analytics
        trackEvent('login_attempt', { method });
        try {
            const loginResponse: LoginResponse = await userLogin(loginPayload);
            if (loginResponse.statusCode === 404 && loginResponse.data.isMigrated) {
                // Account migration required - navigate to migration page directly without error toast
                // The migration page will show an info toast to the user
                trackEvent('login_failed', {
                    method,
                    reason: 'migrated',
                    ...(loginResponse.data?.id && { user_id: loginResponse.data.id }),
                });
                clearMigrationCookiesForCurrentHost();
                navigate(NavigationConfig.UpgradeAnnouncement, {
                    state: {
                        migrationUser: true,
                        email: username,
                        phoneNumber: phoneNumber,
                        id: loginResponse.data?.id,
                    },
                    replace: true,
                });
            } else if (
                IS_MERGED_LOGIN &&
                method === 'phone' &&
                loginResponse.statusCode === 200 &&
                loginResponse.data?.isPhoneVerified === false
            ) {
                // Credentials are valid but the number being used to sign in isn't verified:
                // show the inline verify-mobile panel instead of advancing to the code step.
                setPendingUserId(loginResponse.data?.userId == null ? '' : String(loginResponse.data.userId));
                setPhoneNotRegistered(true);
            } else if (loginResponse.statusCode === 200) {
                trackEvent('login_success', {
                    method,
                    ...(loginResponse.data?.id && { user_id: loginResponse.data.id }),
                });
                clearLoginLockout();
                setIsLoginThroughEmail(method === 'email');
                setIsCalledFromLogin(true);
                // Reset attempt count when opening passcode modal
                setOtpAttemptCount(0);
                setIsOtpDisabled(false);
                setPhoneNotRegistered(false);
                if (IS_MERGED_LOGIN) {
                    // Password is no longer needed once credentials are accepted.
                    setPassword('');
                    const mfaData = loginResponse.data;
                    if (mfaData && typeof mfaData.isPhoneVerified === 'boolean') {
                        // Credentials verified, no code sent yet: let the user choose where to receive it.
                        setMfaOptions({
                            userId: mfaData.userId == null ? '' : String(mfaData.userId),
                            email: mfaData.email ?? '',
                            phoneNumber: mfaData.phoneNumber ?? '',
                            isPhoneVerified: !!mfaData.phoneNumber && mfaData.isPhoneVerified,
                        });
                        setMfaMethod('email');
                        setCurrentStep('two-factor');
                    } else {
                        // Legacy response: the code was already sent to the channel used to sign in.
                        setCurrentStep('security-code');
                    }
                } else {
                    setShowOtpModal(true);
                }
            } else {
                trackEvent('login_failed', {
                    method,
                    reason: loginResponse.message,
                    ...(loginResponse.data?.id && { user_id: loginResponse.data.id }),
                });
                // Don't record failed attempts for non-existent users (statusCode 400)
                const isUserNotFound =
                    loginResponse.statusCode === 400 &&
                    loginResponse.message?.includes('No matching user record found');

                if (isUserNotFound) {
                    // User doesn't exist - don't count as failed attempt
                    showToast(loginResponse.message, 'error');
                }
            }
        } catch (error) {
            const loginErr = error as {
                statusCode?: number;
                message?: string;
                errorCode?: string;
                userId?: number | string;
            };
            trackEvent('login_failed', {
                method,
                reason: loginErr.statusCode === 400 ? loginErr.message : 'exception',
            });
            // Only an explicit backend error code means "phone not verified". The generic
            // "Invalid Phone Number or Password" message can't tell a wrong number from a wrong password.
            const isPhoneNotVerified =
                !!loginErr.errorCode && PHONE_NOT_VERIFIED_ERROR_CODES.includes(loginErr.errorCode);
            if (IS_MERGED_LOGIN && method === 'phone' && isPhoneNotVerified) {
                // Mobile number isn't on file/verified: show the inline panel (password stays populated).
                // The error body carries the account's userId, which the self-verify calls need.
                setPendingUserId(loginErr.userId == null ? '' : String(loginErr.userId));
                setPhoneNotRegistered(true);
                return;
            }
            if (IS_MERGED_LOGIN) {
                setPassword('');
            }
            const locked = recordFailedLoginAttempt();
            const currentAttempts = parseInt(sessionStorage.getItem(LOGIN_FAILED_ATTEMPTS_KEY) || '0', 10);
            const remainingAttempts = MAX_LOGIN_ATTEMPTS - currentAttempts;

            showToast(
                locked
                    ? `Your Account Has Been Locked Due To Multiple Failed Login Attempts. Please Try Again Later Or Contact Support.`
                    : loginErr.statusCode === 400
                      ? `${loginErr.message}. You Have ${remainingAttempts} More Attempt${remainingAttempts !== 1 ? 's' : ''} Remaining.`
                      : `Login Failed. Please Try Again. You Have ${remainingAttempts} More Attempt${remainingAttempts !== 1 ? 's' : ''} Remaining.`,
                'error',
            );
            console.error('Error during login:', error);
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleLoginSubmit = async () => {
        if (isCurrentCredentialLockedOut()) return;
        setPhoneNotRegistered(false);
        if (IS_MERGED_LOGIN) {
            if (!isFormValid()) return;
            if (value === 0) {
                await submitLoginRequest(
                    { email: normalizeEmail(username), password, portalId: resolvedPortalId },
                    'email',
                );
            } else {
                await submitLoginRequest({ phoneNumber, password, portalId: resolvedPortalId }, 'phone');
            }
            return;
        }
        if (value === 0) {
            // login with email
            if (isLoginWithEmailFormValid()) {
                const loginPayload: LoginRequest = {
                    email: username,
                    password,
                    portalId: resolvedPortalId,
                };
                await submitLoginRequest(loginPayload, 'email');
            }
        } else {
            // login with phone number
            if (isLoginWithPhoneNumberFormValid()) {
                const loginPayload: LoginRequest = {
                    phoneNumber: phoneNumber,
                    password,
                    portalId: resolvedPortalId,
                };
                await submitLoginRequest(loginPayload, 'phone');
            }
        }
    };

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        setErrors({});
        setContractId('');
        setUsername('');
        setPassword('');
        setValue(newValue);
        setPhoneNumber('');
    };

    // Single email/mobile field: the detected type decides which state is populated (the other is cleared).
    const handleIdentifierChange = ({ type, email, phone }: ParsedIdentifier) => {
        setErrors({});
        setPhoneNotRegistered(false);
        if (type === 'phone') {
            setValue(1);
            setPhoneNumber(phone);
            setUsername('');
        } else {
            setValue(0);
            setUsername(email);
            setPhoneNumber('');
        }
    };

    // Cancel/back from the post-login screens (or max attempts): return to a fully cleared sign-in form.
    const handleCancelSecurityCode = () => {
        setPassword('');
        setUsername('');
        setPhoneNumber('');
        setValue(0);
        setErrors({});
        setPhoneNotRegistered(false);
        setMfaOptions(null);
        setPendingUserId('');
        setOtpAttemptCount(0);
        setIsOtpDisabled(false);
        setCurrentStep('login');
    };

    // Max wrong attempts: drop the user back to sign-in (the failure toast has already been shown).
    useEffect(() => {
        if (IS_MERGED_LOGIN && isOtpDisabled && currentStep === 'security-code') {
            handleCancelSecurityCode();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOtpDisabled, currentStep]);

    function a11yProps(index: number) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    // Shared keyboard-activation handler for the several clickable "link" texts below
    // (support, register, back-to-login) - Enter/Space triggers the same action as a click.
    const handleActivateOnKeyDown = (action: () => void) => (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            action();
        }
    };
    const setEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        setUsername(e.target.value);
    };

    const handleForgotPassword = () => {
        setErrors({});
        setContractId('');
        setPassword('');
        setCurrentStep('forget-password');
        setForgotPassword(true);
    };

    const handleRegistration = () => {
        navigate(`${NavigationConfig.Registration}?source=login`, { state: { fromLogin: true }, replace: false });
    };
    const handleSupportClick = () => {
        setOpenContactSupportModal(true);
    };

    const handleSupportClose = () => {
        setOpenContactSupportModal(false);
    };
    // Shared OTP-attempt bookkeeping, reused by both the login OTP flow and the
    // forgot-password OTP flow below (the passcode entry UX/rules are identical for both).
    const showMaxOtpAttemptsExceededToast = () => {
        showToast('Maximum Verification Attempts (3) Exceeded. Please Request A New Passcode.', 'error');
    };

    const isOtpVerificationBlocked = () => {
        // Check if passcode verification is disabled due to max attempts
        if (isOtpDisabled || otpAttemptCount >= 3) {
            showMaxOtpAttemptsExceededToast();
            return true;
        }
        return false;
    };

    const showInvalidPasscodeToast = () => {
        showToast('Invalid Passcode. Please Try Again.', 'error');
    };

    // Handles an unsuccessful OTP verification (non-200 response, or a thrown error) - increments
    // the attempt counter, disables further attempts and closes the modal after 3 failures,
    // otherwise shows the failure message with the remaining attempt count.
    const handleOtpAttemptFailure = (message: string | undefined) => {
        const newAttemptCount = otpAttemptCount + 1;
        setOtpAttemptCount(newAttemptCount);

        // Disable after 3 failed attempts (including this one)
        if (newAttemptCount >= 3) {
            setIsOtpDisabled(true);
            showMaxOtpAttemptsExceededToast();
            // Close passcode modal and prevent further attempts
            setShowOtpModal(false);
        } else {
            const remainingAttempts = 3 - newAttemptCount;
            showToast(
                `${message} (${remainingAttempts} Attempt${remainingAttempts > 1 ? 's' : ''} Remaining)`,
                'error',
            );
        }
    };

    const extractOtpErrorMessage = (err: unknown): string | undefined => {
        const otpErr = err as {
            response?: { data?: { message?: string } };
            data?: { message?: string };
            message?: string;
        };
        // api_service rejects with error.response.data (plain object), so check
        // err.message (plain object field), err.data.message, and Axios error path.
        const raw = otpErr?.response?.data?.message || otpErr?.data?.message || otpErr?.message;
        return Array.isArray(raw) ? raw[0] : raw;
    };

    const handleVerifyOtp = async (otp: string) => {
        if (isOtpVerificationBlocked()) {
            return;
        }

        if (otp?.length === 6) {
            try {
                const verifyOtpPayload: { otp: number; portalId: number; email?: string; phoneNumber?: string } = {
                    otp: Number(otp),
                    portalId: resolvedPortalId,
                };
                if (value === 0) {
                    verifyOtpPayload.email = username;
                } else {
                    verifyOtpPayload.phoneNumber = phoneNumber;
                    // verifyOtpPayload.phoneExtension = getDialCodeForApi(countryCode);
                }
                const response = (await verifyLoginOtp(verifyOtpPayload, true)) as unknown as LoginApiResponse;
                if (response.statusCode === 200) {
                    // Mark this browser/domain as migrated after first successful login.
                    // This ensures future visits land on /login, not /update_portal.
                    setCurrentDomainMigrationCookie({ isMigrated: true });

                    // Reset attempt count on success
                    setOtpAttemptCount(0);
                    setIsOtpDisabled(false);
                    setShowOtpModal(false);
                    // Ensure no prior account conversation is visible after a new login.
                    dispatch(resetChatbot());
                    // Always rotate chatbot session on login so Session_id cannot carry over.
                    dispatch(initializeSession(chatbotService.generateSessionId()));
                    // A real customer login must never inherit a stale persisted impersonation flag,
                    // which would disable payment submission.
                    sessionStorage.removeItem('impersonationToken');
                    sessionStorage.removeItem('impersonationStartTime');
                    dispatch(clearImpersonation());
                    dispatch(updateLoginStatus(true));
                    dispatch(
                        updateLoginUserDetails({
                            token: (response.token || response.data?.token) ?? '',
                            refreshToken: response.refreshToken || response.data?.refreshToken || null,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            user: (response.user || response.data?.user || response.data) as any,
                        }),
                    );
                    dispatch(
                        setLoginUserProfileImageUrl(
                            response?.data?.user?.ProfileImage || response?.user?.ProfileImage
                                ? `${process.env.REACT_APP_PROFILE_URL}${response?.data?.user?.ProfileImage || response?.user?.ProfileImage}`
                                : '',
                        ),
                    );

                    // Track successful login (login_success already tracked in submitLoginRequest)
                    const userId = response?.data?.user?.Id || response?.user?.Id;
                    if (userId) {
                        setUserId(String(userId));
                    }

                    // Check if there's a stored contractID for AutoPay link
                    const pendingContractId = sessionStorage.getItem(AUTOPAY_CONTRACTID_STORAGE_KEY);
                    if (pendingContractId) {
                        sessionStorage.removeItem(AUTOPAY_CONTRACTID_STORAGE_KEY);
                        // Validate and redirect to contract details with autopay
                        handleAutopayAfterLogin(pendingContractId);
                    } else {
                        // Defer navigation to App.tsx's isLoggedIn effect instead of navigating here:
                        // navigating synchronously alongside the dispatches above races
                        // PostAuthenticatedRoutes' own "/login -> ContractOverview" redirect for
                        // already-authenticated users (which can win if it mounts while the router's
                        // location hasn't yet caught up to a navigate() called in the same tick).
                        markJustLoggedIn();
                    }
                    // calling notifications count API
                } else {
                    handleOtpAttemptFailure(response.message);
                }
            } catch (err) {
                handleOtpAttemptFailure(extractOtpErrorMessage(err) || 'Invalid Passcode. Please Try Again.');
            } finally {
                dispatch(hideLoader());
            }
        } else {
            showInvalidPasscodeToast();
        }
    };

    const handleVerifyOtpForForgotPassword = async (otp: string) => {
        if (isOtpVerificationBlocked()) {
            return;
        }

        if (otp?.length === 6) {
            try {
                const verifyOtpPayload = {
                    otp: Number(otp),
                    email: username,
                    portalId: resolvedPortalId,
                };
                const response = (await verifyForgotPasswordOtp(verifyOtpPayload, true)) as unknown as LoginApiResponse;
                if (response.statusCode === 200) {
                    // Reset attempt count on success
                    setOtpAttemptCount(0);
                    setIsOtpDisabled(false);
                    setShowOtpModal(false);
                    setPasswordOtpSuccess(true);
                } else {
                    handleOtpAttemptFailure(response.message);
                }
            } catch (err) {
                handleOtpAttemptFailure(extractOtpErrorMessage(err) || 'Invalid Passcode. Please Try Again.');
            } finally {
                dispatch(hideLoader());
            }
        } else {
            showInvalidPasscodeToast();
        }
    };
    const handleCloseOtpModal = () => {
        setShowOtpModal(false);
        // Reset attempt count when closing modal
        setOtpAttemptCount(0);
        setIsOtpDisabled(false);
    };

    const gotoLogin = () => {
        resetForm();
        navigate(NavigationConfig.Login);
        setCurrentStep('login');
    };

    const resetForm = () => {
        setUsername('');
        setPassword('');
        setContractId('');
        setValue(0);
        setIsCalledFromLogin(false);
        setIsLoginThroughEmail(null);
    };

    // method to set the password after forgot password
    const handlePasswordSetupComplete = async (password: string) => {
        try {
            const setPasswordPayload = {
                email: username,
                password: password,
                portalId: resolvedPortalId,
            };
            const response = (await resetForgotPassword(setPasswordPayload, true)) as unknown as LoginApiResponse;
            if (response.statusCode === 200) {
                // Track password reset via forgot password flow
                trackPasswordChange();

                setPasswordOtpSuccess(false);
                setCurrentStep('password-success');
            } else {
                showToast(response.message, 'error');
            }
        } catch (error) {
            console.log(error, 'error in set password');
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleResendOTP = async () => {
        setTimerCount(0);
        // Reset attempt count when resending passcode
        setOtpAttemptCount(0);
        setIsOtpDisabled(false);
        try {
            const resendOtpPayload: ResendOtpRequest & { phoneExtension?: string } = {
                portalId: resolvedPortalId,
            };
            if (value === 0) {
                resendOtpPayload.email = username;
            } else {
                resendOtpPayload.phoneNumber = phoneNumber;
                // resendOtpPayload.phoneExtension = getDialCodeForApi(countryCode);
            }
            const response = (await resendLoginOtp(resendOtpPayload, true)) as unknown as LoginApiResponse;
            if (response.statusCode === 200) {
                showToast(response.message, 'success');
                setTimerCount(defaultTimerCount);
                // Reset attempt count when resending passcode (already done in handleResendOTP, but ensure it's set)
                setOtpAttemptCount(0);
                setIsOtpDisabled(false);
                setShowOtpModal(true);
            } else {
                showToast(response.message, 'error');
            }
        } catch (err) {
            const resendErr = err as { message?: string };
            showToast(resendErr.message, 'error');
        } finally {
            dispatch(hideLoader());
        }
    };

    // Sends the passcode to the method chosen on the two-factor screen, then moves to code entry.
    // Verify/resend key off `value` (0 = email, 1 = phone) plus the matching identifier, so set both here.
    const handleSendCode = async () => {
        if (!mfaOptions || isSendingCode) return;
        const viaSms = mfaMethod === 'sms' && mfaOptions.isPhoneVerified;
        setIsSendingCode(true);
        try {
            // The server re-resolves the user by email and re-checks the phone is verified for SMS.
            const payload = {
                portalId: resolvedPortalId,
                email: mfaOptions.email,
                otpChannel: viaSms ? 'sms' : 'email',
            } as const;
            const response = (await sendLoginOtp(payload, true)) as unknown as LoginApiResponse;
            if (response.statusCode === 200 && response.data?.otpSent !== false) {
                setUsername(viaSms ? '' : mfaOptions.email);
                setPhoneNumber(viaSms ? mfaOptions.phoneNumber : '');
                setValue(viaSms ? 1 : 0);
                setIsLoginThroughEmail(!viaSms);
                setIsCalledFromLogin(true);
                setOtpAttemptCount(0);
                setIsOtpDisabled(false);
                setCurrentStep('security-code');
            } else {
                showToast(response.message, 'error');
            }
        } catch (err) {
            showToast(extractOtpErrorMessage(err) || "We couldn't send your code. Please try again.", 'error');
        } finally {
            setIsSendingCode(false);
            dispatch(hideLoader());
        }
    };

    // Self-verification of an unverified primary mobile number. Reachable from the two-factor screen
    // and from the login screen's "not registered" panel; `mobileVerify.origin` decides where to return.
    const sendMobileVerificationCode = async (
        userId: string,
        phone: string,
        origin: 'two-factor' | 'login',
    ): Promise<boolean> => {
        try {
            const response = (await sendSelfVerifyPhoneOtp(
                { userId, phoneNumber: phone, portalId: resolvedPortalId },
                true,
            )) as unknown as LoginApiResponse & { data?: { alreadyVerified?: boolean } };
            if (response.statusCode !== 200) {
                showToast(response.message, 'error');
                return false;
            }
            // 200 with otpSent === false: the SMS provider failed, so no code is on its way.
            if (response.data?.otpSent === false) {
                showToast(response.message, 'error');
                return false;
            }
            showToast(response.message, 'success');
            if (response.data?.alreadyVerified) {
                finishMobileVerification(origin);
                return false;
            }
            return true;
        } catch (err) {
            showToast(extractOtpErrorMessage(err) || "We couldn't send your code. Please try again.", 'error');
            return false;
        } finally {
            dispatch(hideLoader());
        }
    };

    const finishMobileVerification = (origin: 'two-factor' | 'login') => {
        setMobileVerify(null);
        if (origin === 'two-factor') {
            setMfaOptions(prev => (prev ? { ...prev, isPhoneVerified: true } : prev));
            setMfaMethod('sms');
            setCurrentStep('two-factor');
        } else {
            setPhoneNotRegistered(false);
            setCurrentStep('login');
        }
    };

    const handleStartMobileVerification = async (userId: string, phone: string, origin: 'two-factor' | 'login') => {
        if (!phone) return;
        if (!userId) {
            showToast("We couldn't verify your account. Please sign in again.", 'error');
            return;
        }
        if (await sendMobileVerificationCode(userId, phone, origin)) {
            setMobileVerify({ userId, phone, origin });
        }
    };

    const handleConfirmMobileVerification = async (otp: string) => {
        if (!mobileVerify) return;
        try {
            const response = (await confirmSelfVerifyPhoneOtp(
                {
                    userId: mobileVerify.userId,
                    phoneNumber: mobileVerify.phone,
                    otp: Number(otp),
                    portalId: resolvedPortalId,
                },
                true,
            )) as unknown as LoginApiResponse;
            if (response.statusCode === 200) {
                finishMobileVerification(mobileVerify.origin);
                showToast(
                    mobileVerify.origin === 'login'
                        ? t('authFlow.verifyMobile.successLogin')
                        : t('authFlow.verifyMobile.success'),
                    'success',
                );
            } else {
                showToast(response.message, 'error');
            }
        } catch (err) {
            showToast(extractOtpErrorMessage(err) || 'Invalid Passcode. Please Try Again.', 'error');
        } finally {
            dispatch(hideLoader());
        }
    };

    // Shared "trouble activation / contact support" link, rendered identically on both the
    // login step and the forgot-password step.
    // Merged login only: bold navy action links with muted lead-in text (shared styles stay untouched).
    const mergedFooterLinkSx = IS_MERGED_LOGIN
        ? {
              fontWeight: 700,
              color: colors.bluePreset[10],
              '&:hover, &:visited, &:active': { color: colors.bluePreset[10] },
              '&:hover': { textDecoration: 'underline' },
          }
        : undefined;
    const mergedLegalLinkSx = IS_MERGED_LOGIN
        ? {
              color: colors.bluePreset[10],
              '&:hover, &:visited, &:active': { color: colors.bluePreset[10] },
              '&:hover': { textDecoration: 'underline' },
          }
        : undefined;
    const mergedFooterTextSx = IS_MERGED_LOGIN
        ? { maxWidth: '100%', whiteSpace: 'normal', textWrap: 'balance' }
        : undefined;

    const legalLinks = (
        <FooterLinksContainer>
            <FooterTypography variant="overline">
                <LinkTextWrapper
                    sx={theme => ({
                        ...FooterLinkText(theme),
                        ...mergedLegalLinkSx,
                    })}
                    target="_blank"
                    href="https://auxcap.com/terms-of-use/"
                >
                    {t('registration.links.termsOfUse')}
                </LinkTextWrapper>
                <SeparatorText variant="overline" color="textPrimary">
                    {' | '}
                </SeparatorText>
                <LinkTextWrapper
                    sx={theme => ({
                        ...FooterLinkText(theme),
                        ...mergedLegalLinkSx,
                    })}
                    target="_blank"
                    href="https://auxcap.com/privacy-policy/"
                >
                    {t('registration.links.privacyPolicy')}
                </LinkTextWrapper>
            </FooterTypography>
        </FooterLinksContainer>
    );

    const supportContactLink = (
        <SupportText variant="body2" whiteSpace={{ xs: 'wrap', md: 'nowrap' }} sx={mergedFooterTextSx}>
            {t('registration.links.troubleActivation')}{' '}
            <LinkTextWrapper
                sx={mergedFooterLinkSx}
                onClick={handleSupportClick}
                onKeyDown={handleActivateOnKeyDown(handleSupportClick)}
                tabIndex={0}
                role="button"
            >
                {t('registration.links.contactSupport')}
            </LinkTextWrapper>
        </SupportText>
    );

    return (
        <CommonLoginWrapper>
            {isBrandLoading ? (
                <Stack
                    className="loginCard"
                    sx={{
                        maxWidth: '458px !important',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 200,
                    }}
                >
                    <CircularProgress size={36} thickness={3} sx={{ color: 'primary.main' }} />
                </Stack>
            ) : (
                <>
                    {/* Main Content Card */}
                    <Stack
                        className="loginCard"
                        sx={{ maxWidth: `${passwordOtpSuccess ? '800px' : '458px'} !important` }}
                    >
                        <Stack sx={{ my: 3 }}>
                            <BrandLogo
                                alt="Auxcap logo"
                                style={{ height: '100%', width: '100%', maxHeight: 60, maxWidth: 188 }}
                            />
                        </Stack>
                        <Stack
                            className={
                                currentStep === 'login' ||
                                passwordOtpSuccess ||
                                (currentStep === 'forget-email' && contractId)
                                    ? 'commonLoginCard loginCardBody'
                                    : 'commonLoginCard'
                            }
                        >
                            {currentStep === 'login' && (
                                <>
                                    <Typography variant="h5" className="mainTitle">
                                        {t('authFlow.login.title')}
                                    </Typography>
                                    {IS_MERGED_LOGIN ? (
                                        <Typography variant="body2" color="textSecondary" textAlign={'center'} mb={3}>
                                            {t('authFlow.login.subtitle')}
                                        </Typography>
                                    ) : (
                                        <>
                                            <Typography variant="body2" color="textPrimary" textAlign={'center'} mb={1}>
                                                Please enter the details used during registration to access your
                                                account.
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                textAlign={'center'}
                                                mb={3}
                                            >
                                                Enter your registered email or Phone Number associated with your
                                                Contract ID
                                            </Typography>
                                        </>
                                    )}
                                    {isCurrentCredentialLockedOut() && (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            <Typography variant="body2" component="span">
                                                Your account has been locked due to multiple failed login attempts.
                                                Please try again later or contact support. Login will be available again
                                                in <strong>{formatLockoutRemaining(lockoutRemainingSeconds)}</strong>.
                                            </Typography>
                                        </Alert>
                                    )}
                                    {IS_MERGED_LOGIN && (
                                        <MergedLoginForm
                                            mode={value === 0 ? 'email' : 'phone'}
                                            email={username}
                                            phone={phoneNumber}
                                            password={password}
                                            onIdentifierChange={handleIdentifierChange}
                                            onPasswordChange={setPassword}
                                            onSubmit={handleLoginSubmit}
                                            onForgotPassword={handleForgotPassword}
                                            onUpdatePhone={() => setCurrentStep('update-phone')}
                                            submitDisabled={!isFormValid() || isCurrentCredentialLockedOut()}
                                            phoneNotRegistered={phoneNotRegistered}
                                            onVerifyMobile={() =>
                                                handleStartMobileVerification(pendingUserId, phoneNumber, 'login')
                                            }
                                        />
                                    )}
                                    {!IS_MERGED_LOGIN && (
                                        <Stack sx={{ width: '100%' }}>
                                            <LoginTabsWrapper>
                                                <Tabs
                                                    value={value}
                                                    onChange={handleChange}
                                                    aria-label="Login with email or phone number"
                                                >
                                                    <Tab label="Email" {...a11yProps(0)} tabIndex={0} />
                                                    <Tab label="Phone Number" {...a11yProps(1)} tabIndex={0} />
                                                </Tabs>
                                            </LoginTabsWrapper>
                                            <CustomTabPanel value={value} index={0}>
                                                <Stack component="form" gap={2}>
                                                    <TextFieldComponent
                                                        type="email"
                                                        onChange={setEmail}
                                                        value={username}
                                                        error={errors?.email ? true : false}
                                                        errorValue={errors?.email}
                                                        maxLength={50}
                                                        autoComplete="off"
                                                        label="Email Address"
                                                    />

                                                    <LoginPasswordStrengthField
                                                        value={password}
                                                        onChange={e => setPassword(e.target.value)}
                                                        error={errors?.password ? true : false}
                                                        errorValue={errors?.password}
                                                        onForgotPassword={handleForgotPassword}
                                                        maxLength={20}
                                                        autoComplete="new-password"
                                                    />
                                                </Stack>
                                            </CustomTabPanel>
                                            <CustomTabPanel value={value} index={1}>
                                                <Stack gap={2} component="form" noValidate>
                                                    <PhoneInputComponent
                                                        label="Phone Number"
                                                        countryCode={countryCode}
                                                        onCountryChange={setCountryCode}
                                                        value={phoneNumber}
                                                        onChange={e => setPhoneNumber(e.target.value)}
                                                        error={errors?.phoneNumber ? true : false}
                                                        errorValue={errors?.phoneNumber}
                                                        maxLength={10}
                                                    />
                                                    <LoginPasswordStrengthField
                                                        value={password}
                                                        onChange={e => setPassword(e.target.value)}
                                                        error={errors?.password ? true : false}
                                                        errorValue={errors?.password}
                                                        onForgotPassword={handleForgotPassword}
                                                        maxLength={20}
                                                    />
                                                </Stack>
                                            </CustomTabPanel>
                                        </Stack>
                                    )}
                                    <Stack sx={{ mt: IS_MERGED_LOGIN ? 0 : 3, alignItems: 'center' }}>
                                        {!IS_MERGED_LOGIN && (
                                            <CustomButton
                                                aria-label="Login"
                                                onClick={handleLoginSubmit}
                                                disabled={!isFormValid() || isCurrentCredentialLockedOut()}
                                            >
                                                Login
                                            </CustomButton>
                                        )}
                                        {/* <CustomButton
                                    aria-label="Open Ello Make Payments"
                                    variant="outlined"
                                    onClick={() =>
                                        navigate(
                                            `${NavigationConfig.ElloPaymentConfirm}?paymentId=AC1805&token=9c73teFa-kMsmOdOe54UclRUZZAlpVVTv4fKQ_nDOgg`,
                                        )
                                    }
                                    sx={{ mt: 1.5 }}
                                >
                                    Open Ello Make Payments
                                </CustomButton> */}
                                        <SupportLinksStack sx={IS_MERGED_LOGIN ? { pt: 2.5, gap: 1.5 } : undefined}>
                                            {supportContactLink}
                                            <SupportText variant="body2" textAlign={'center'}>
                                                {t('registration.links.dontHaveAccount')}{' '}
                                                <LinkTextWrapper
                                                    onClick={handleRegistration}
                                                    sx={theme => ({ ...LoginLinkText(theme), ...mergedFooterLinkSx })}
                                                    tabIndex={0}
                                                    role="button"
                                                    onKeyDown={handleActivateOnKeyDown(handleRegistration)}
                                                >
                                                    {t('registration.links.register')}
                                                </LinkTextWrapper>
                                            </SupportText>
                                            {legalLinks}
                                        </SupportLinksStack>
                                    </Stack>
                                </>
                            )}
                            {currentStep === 'two-factor' && IS_MERGED_LOGIN && mfaOptions && (
                                <TwoFactorMethodPicker
                                    maskedEmail={maskEmail(mfaOptions.email)}
                                    maskedPhone={mfaOptions.phoneNumber ? maskPhone(mfaOptions.phoneNumber) : ''}
                                    isPhoneVerified={mfaOptions.isPhoneVerified}
                                    selected={mfaMethod}
                                    onSelect={setMfaMethod}
                                    sending={isSendingCode}
                                    onSend={handleSendCode}
                                    onVerifyMobile={() =>
                                        handleStartMobileVerification(
                                            mfaOptions.userId,
                                            mfaOptions.phoneNumber,
                                            'two-factor',
                                        )
                                    }
                                    onUpdatePhone={() => setCurrentStep('update-phone')}
                                    onCancel={handleCancelSecurityCode}
                                />
                            )}
                            {currentStep === 'update-phone' && IS_MERGED_LOGIN && (
                                <UpdatePhoneFlow
                                    portalId={resolvedPortalId}
                                    showToast={showToast}
                                    onExit={handleCancelSecurityCode}
                                    onSuccess={() => {
                                        handleCancelSecurityCode();
                                        showToast(t('authFlow.updatePhone.success'), 'success');
                                    }}
                                />
                            )}
                            {currentStep === 'security-code' && IS_MERGED_LOGIN && (
                                <SecurityCodeStep
                                    smsLast4={value === 1 ? phoneNumber.slice(-4) : undefined}
                                    expiryMinutes={LOGIN_OTP_EXPIRY_MINUTES}
                                    resendCooldownSeconds={defaultTimerCount}
                                    onVerify={handleVerifyOtp}
                                    onResend={handleResendOTP}
                                    onCancel={handleCancelSecurityCode}
                                />
                            )}
                            {currentStep === 'forget-password' && !passwordOtpSuccess && (
                                <Box width={'100%'}>
                                    <Typography variant="h5" component="h1" className="mainTitle">
                                        {' '}
                                        Forgot Password{' '}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" mb={3} textAlign={'center'}>
                                        Enter your account email address to receive a password reset code.
                                    </Typography>
                                    <Stack gap={3} component="form" noValidate>
                                        <TextFieldComponent
                                            label="Email"
                                            required
                                            onChange={setEmail}
                                            error={errors?.email ? true : false}
                                            errorValue={errors?.email}
                                            value={username}
                                            maxLength={50}
                                            autoComplete="off"
                                        />
                                        <CustomButton
                                            aria-label="Send Passcode"
                                            onClick={sendRestLink}
                                            disabled={!username.trim() || !regex.emailRegExp.test(username)}
                                        >
                                            Send Passcode
                                        </CustomButton>
                                    </Stack>
                                    <Stack sx={{ mt: 4, alignItems: 'center', gap: 1.25 }}>
                                        {supportContactLink}
                                        <Typography variant="body2" sx={{ color: colors.greyPreset[80] }}>
                                            Remember your password?{' '}
                                            <LinkTextWrapper
                                                onClick={handleLogin}
                                                role="button"
                                                onKeyDown={handleActivateOnKeyDown(handleLogin)}
                                                tabIndex={0}
                                                sx={{
                                                    ...theme.typography.body2,
                                                    ...mergedFooterLinkSx,
                                                }}
                                            >
                                                Back to Login
                                            </LinkTextWrapper>
                                        </Typography>
                                        {legalLinks}
                                    </Stack>
                                </Box>
                            )}
                            {forgotPassword && passwordOtpSuccess && (
                                <PasswordSetup
                                    forgotPassword={forgotPassword}
                                    onPasswordSetupComplete={handlePasswordSetupComplete}
                                    onBack={() => setCurrentStep('verify')}
                                />
                            )}
                            {currentStep === 'password-success' && (
                                <Box width={'100%'}>
                                    {/* Success Title */}
                                    <Typography textAlign={'center'} component="h1" variant="h5" className="mainTitle">
                                        Password reset successful
                                    </Typography>

                                    {/* Success Message */}
                                    <Typography
                                        variant="body2"
                                        color={colors.greyPreset[80]}
                                        mb={3}
                                        textAlign={'center'}
                                    >
                                        Your password has been updated. You can now Login with your new password.{' '}
                                    </Typography>

                                    {/* Go to Login Button */}
                                    <CustomButton aria-label="Go to Login" variant="contained" onClick={gotoLogin}>
                                        Go to Login
                                    </CustomButton>
                                </Box>
                            )}
                        </Stack>
                    </Stack>
                    {/* Passcode modal */}
                    <Dialog
                        open={IS_MERGED_LOGIN && !!mobileVerify}
                        onClose={(_event, reason) => {
                            if (reason === 'backdropClick') return;
                            setMobileVerify(null);
                        }}
                        sx={{ '& .MuiDialog-paper': { borderRadius: 4, p: 3, m: 2, width: '100%', maxWidth: 458 } }}
                    >
                        {mobileVerify && (
                            <SecurityCodeStep
                                title={t('authFlow.verifyMobile.title')}
                                description={t('authFlow.verifyMobile.body').replace(
                                    '{{last4}}',
                                    mobileVerify.phone.slice(-4),
                                )}
                                expiryMinutes={LOGIN_OTP_EXPIRY_MINUTES}
                                resendCooldownSeconds={defaultTimerCount}
                                onVerify={handleConfirmMobileVerification}
                                onResend={async () => {
                                    await sendMobileVerificationCode(
                                        mobileVerify.userId,
                                        mobileVerify.phone,
                                        mobileVerify.origin,
                                    );
                                }}
                                onCancel={() => setMobileVerify(null)}
                            />
                        )}
                    </Dialog>
                    <Dialog
                        open={showOtpModal && !(IS_MERGED_LOGIN && isCalledFromLogin)}
                        onClose={(_event, reason) => {
                            // Only allow closing via close button, not backdrop click or escape key
                            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                                return;
                            }
                            handleCloseOtpModal();
                        }}
                        disableEscapeKeyDown
                        sx={{
                            '& .MuiDialog-paper': {
                                borderRadius: 4,
                                padding: 3,
                                maxWidth: '400px',
                                width: '100%',
                            },
                        }}
                        aria-labelledby="otp-dialog-title"
                        aria-describedby="otp-dialog-description"
                    >
                        <OtpInput
                            mainTitle="Enter Passcode"
                            email={username}
                            // update the subtitle like below to make it dynamic
                            subtitle={
                                <>
                                    Please enter a {6}-digit passcode sent to registered{' '}
                                    {isLoginThroughEmail || currentStep === 'forget-password' ? 'email' : 'mobile'} -{' '}
                                    {isLoginThroughEmail || currentStep === 'forget-password' ? (
                                        getMaskedEmailForProp(username)
                                    ) : (
                                        <span style={{ fontFamily: theme.typography.fontFamily }}>
                                            {getMaskedPhoneNumberForProp(phoneNumber)}
                                        </span>
                                    )}
                                </>
                            }
                            length={6}
                            onChange={() => {}}
                            onVerify={otp =>
                                isCalledFromLogin ? handleVerifyOtp(otp) : handleVerifyOtpForForgotPassword(otp)
                            }
                            verifyButtonText="Verify"
                            onClose={handleCloseOtpModal}
                            onResend={() => {
                                handleResendOTP();
                            }}
                            timerCount={timerCount}
                            resendTextWithTimer={'Resend Passcode'}
                            isCalledFromLogin={isCalledFromLogin}
                            isVerifyDisabled={isOtpDisabled}
                            attemptCount={otpAttemptCount}
                        />
                    </Dialog>
                    <CommonModal
                        modalOpen={openContactSupportModal}
                        onCloseModal={handleSupportClose}
                        DialogMainTitle={t('registration.contactSupport.title')}
                        showPrimaryButton={false}
                        secondaryBtnName="Got It"
                        secondaryBtnClick={handleSupportClose}
                        ModalMaxWidth={400}
                    >
                        <ContactSupportModalContent />
                    </CommonModal>
                </>
            )}
        </CommonLoginWrapper>
    );
};
